import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Loader2, Search, X } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Field, Section, Toggle } from '@/components/ui/editor-fields';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { useBusinesses } from '@/features/business/use-businesses';
import { useCustomerSearch } from '@/features/customers/customers.api';
import { formatCurrency, initials } from '@/lib/format';
import { formatStoreTime, toApiDate } from '@/lib/store-time';
import {
  useCreatePromoCode,
  useUpdateExpiry,
  type CodeKind,
} from './promo-codes.api';
import {
  APPLICATION_TYPE,
  APPLIES_TO,
  emptyCode,
  type PromoCode,
  type PromoCodeInput,
} from './types';

/**
 * Create a code, or move an existing one's expiry.
 *
 * Those really are the only two things the API offers: `discountcode/edit`
 * takes `{ id, expiresAt }`. The original still rendered the full form when you
 * opened a saved code, with every other box editable and silently discarded on
 * save — so the drawer shows the rest as a read-only summary instead.
 *
 * It also seeded that form with `moment(expiresAt).add(-1, 'days')` and posted
 * the result back unchanged, so opening a code and saving it pulled the expiry
 * a day earlier every time. The date here round-trips.
 */

const GLOBAL = 'GLOBAL';

/** A picked day, as the instant the API stores. Inclusive of that whole day. */
const dayToApi = (value: string) => (value ? toApiDate(value, true) : null);

export interface EditorTarget {
  kind: CodeKind;
  /** The saved code being edited, or null to create one. */
  existing: PromoCode | null;
}

export function PromoCodeEditor({
  target,
  open,
  onOpenChange,
}: {
  target: EditorTarget | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();

  const [draft, setDraft] = useState<PromoCodeInput | null>(null);
  const [expiry, setExpiry] = useState('');
  const [notify, setNotify] = useState(false);
  const [customer, setCustomer] = useState<{
    id: string;
    name: string;
    email: string | null;
    avatar?: string | null;
  } | null>(null);
  const [userDraft, setUserDraft] = useState('');
  const [userTerm, setUserTerm] = useState('');

  const { data: businesses } = useBusinesses();
  const userSearch = useCustomerSearch(userTerm);
  const create = useCreatePromoCode();
  const updateExpiry = useUpdateExpiry();

  const existing = target?.existing ?? null;
  const kind = target?.kind ?? 'store';
  const isEdit = !!existing;

  useEffect(() => {
    setDraft(target && !target.existing ? emptyCode(target.kind) : null);
    setExpiry(
      target?.existing?.expiresAt
        ? formatStoreTime(target.existing.expiresAt, true)
        : ''
    );
    setNotify(false);
    setCustomer(null);
    setUserDraft('');
    setUserTerm('');
  }, [target]);

  const busy = create.isPending || updateExpiry.isPending;

  const set = <K extends keyof PromoCodeInput>(
    key: K,
    value: PromoCodeInput[K]
  ) => setDraft((current) => (current ? { ...current, [key]: value } : current));

  const failed = (error: unknown) =>
    toast.error(
      error instanceof Error ? error.message : t('promoCodes.saveFailed')
    );

  function submit() {
    if (isEdit) {
      updateExpiry.mutate(
        { id: existing!.id, expiresAt: dayToApi(expiry) },
        {
          onSuccess: () => {
            toast.success(t('promoCodes.expiryUpdated'));
            onOpenChange(false);
          },
          onError: failed,
        }
      );
      return;
    }

    create.mutate(
      {
        ...draft!,
        expiresAt: dayToApi(expiry),
        userId: kind === 'customer' ? (customer?.id ?? null) : null,
        // The original kept these in step; the redemption matcher reads
        // `matchString`, so a code whose two halves disagree never matches.
        matchString: draft!.code,
        // The switch asks whether to tell the customer; the field records the
        // opposite, which is why the original inverted it on the way out.
        silentMode: kind === 'customer' ? !notify : true,
        message: kind === 'customer' && notify ? draft!.message : null,
      },
      {
        onSuccess: () => {
          toast.success(t('promoCodes.created'));
          onOpenChange(false);
        },
        onError: failed,
      }
    );
  }

  const canSave = isEdit
    ? true
    : !!draft?.code.trim() &&
      draft.value > 0 &&
      (kind === 'store' || !!customer);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full gap-0 overflow-y-auto p-0 sm:max-w-lg"
      >
        <SheetHeader className="border-b pr-12">
          <SheetTitle>
            {isEdit
              ? t('promoCodes.editCode')
              : kind === 'store'
                ? t('promoCodes.newStoreCode')
                : t('promoCodes.newCustomerCode')}
          </SheetTitle>
        </SheetHeader>

        {/* ------------------------------------------------------- editing */}
        {isEdit && existing && (
          <div className="space-y-6 p-4">
            <p className="bg-muted text-muted-foreground rounded-xl p-3 text-sm">
              {t('promoCodes.expiryOnlyNotice')}
            </p>

            <dl className="divide-y rounded-xl border text-sm">
              {[
                [t('promoCodes.code'), existing.code],
                [
                  kind === 'store' ? t('promoCodes.store') : t('promoCodes.customer'),
                  kind === 'store'
                    ? (existing.businessName ?? t('promoCodes.global'))
                    : existing.customerName,
                ],
                [t('promoCodes.value'), formatCurrency(existing.value)],
                [
                  t('promoCodes.minPurchase'),
                  existing.minimumAmount > 0
                    ? formatCurrency(existing.minimumAmount)
                    : t('promoCodes.noMinimum'),
                ],
                [
                  t('promoCodes.appliesTo'),
                  t(`promoCodes.appliesTo${existing.appliesTo}`),
                ],
                [
                  t('promoCodes.usageLimit'),
                  t(`promoCodes.usage${existing.applicationType}`),
                ],
              ].map(([label, value]) => (
                <div
                  key={String(label)}
                  className="flex items-baseline justify-between gap-3 p-3"
                >
                  <dt className="text-muted-foreground shrink-0 text-xs font-semibold">
                    {label}
                  </dt>
                  <dd className="min-w-0 truncate text-right">
                    {value || '—'}
                  </dd>
                </div>
              ))}
            </dl>

            <Field
              label={t('promoCodes.expires')}
              htmlFor="expiry"
              hint={t('promoCodes.expiryHint')}
            >
              <Input
                id="expiry"
                type="date"
                value={expiry}
                disabled={busy}
                onChange={(event) => setExpiry(event.target.value)}
              />
            </Field>
          </div>
        )}

        {/* ------------------------------------------------------ creating */}
        {!isEdit && draft && (
          <div className="space-y-6 p-4">
            {kind === 'customer' && (
              <Section title={t('promoCodes.customer')}>
                {customer ? (
                  <div className="flex items-center gap-2.5 rounded-xl border p-2.5">
                    <Avatar className="size-9 shrink-0">
                      {customer.avatar && (
                        <AvatarImage src={customer.avatar} alt="" />
                      )}
                      <AvatarFallback>
                        {initials(customer.name.split(' ')[0], customer.name.split(' ')[1])}
                      </AvatarFallback>
                    </Avatar>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm">{customer.name}</p>
                      <p className="text-muted-foreground truncate text-xs">
                        {customer.email}
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      aria-label={t('promoCodes.clearCustomer')}
                      disabled={busy}
                      onClick={() => setCustomer(null)}
                    >
                      <X className="size-4" />
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <form
                      role="search"
                      className="flex gap-2"
                      onSubmit={(event) => {
                        event.preventDefault();
                        setUserTerm(userDraft.trim());
                      }}
                    >
                      <div className="relative flex-1">
                        <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2" />
                        <Input
                          value={userDraft}
                          onChange={(event) => setUserDraft(event.target.value)}
                          placeholder={t('promoCodes.searchCustomer')}
                          aria-label={t('promoCodes.searchCustomer')}
                          className="pl-9"
                        />
                      </div>
                      <Button type="submit" disabled={userSearch.isFetching}>
                        {userSearch.isFetching ? (
                          <Loader2 className="animate-spin" />
                        ) : (
                          t('common.search')
                        )}
                      </Button>
                    </form>

                    {/* `isLoading`: the search waits on a term. A new term has
                        no answer to keep on screen, so stand in for it. */}
                    {userSearch.isLoading && (
                      <div className="divide-y rounded-xl border">
                        {Array.from({ length: 3 }).map((_, index) => (
                          <div
                            key={index}
                            className="flex items-center gap-2.5 p-2.5"
                          >
                            <Skeleton className="size-8 shrink-0 rounded-full" />
                            <div className="flex-1 space-y-1.5">
                              <Skeleton className="h-3.5 w-1/2" />
                              <Skeleton className="h-3 w-2/3" />
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {userSearch.isSuccess &&
                      (userSearch.data?.results ?? []).length === 0 && (
                        <p className="text-muted-foreground text-xs">
                          {t('promoCodes.noCustomers')}
                        </p>
                      )}

                    {(userSearch.data?.results ?? []).length > 0 && (
                      <div className="max-h-56 divide-y overflow-y-auto rounded-xl border">
                        {userSearch.data!.results.map((user) => (
                          <button
                            key={user.id}
                            type="button"
                            className="hover:bg-accent flex w-full items-center gap-2.5 p-2.5 text-left transition-colors"
                            onClick={() =>
                              setCustomer({
                                id: user.id,
                                name:
                                  [user.firstName, user.lastName]
                                    .filter(Boolean)
                                    .join(' ') || '—',
                                email: user.email,
                                avatar: user.avatar,
                              })
                            }
                          >
                            <Avatar className="size-8 shrink-0">
                              {user.avatar && (
                                <AvatarImage src={user.avatar} alt="" />
                              )}
                              <AvatarFallback>
                                {initials(user.firstName, user.lastName)}
                              </AvatarFallback>
                            </Avatar>
                            <div className="min-w-0">
                              <p className="truncate text-sm">
                                {[user.firstName, user.lastName]
                                  .filter(Boolean)
                                  .join(' ') || '—'}
                              </p>
                              <p className="text-muted-foreground truncate text-xs">
                                {user.email}
                              </p>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </Section>
            )}

            <Section title={t('promoCodes.theCode')}>
              <Field
                label={t('promoCodes.code')}
                htmlFor="code"
                hint={t('promoCodes.codeHint')}
              >
                <Input
                  id="code"
                  value={draft.code}
                  disabled={busy}
                  className="font-mono uppercase"
                  onChange={(event) =>
                    set('code', event.target.value.toUpperCase())
                  }
                />
              </Field>

              <div className="grid gap-3 sm:grid-cols-2">
                <Field label={t('promoCodes.value')} htmlFor="value">
                  <Input
                    id="value"
                    type="number"
                    min={0}
                    step="0.01"
                    value={draft.value}
                    disabled={busy}
                    onChange={(event) => set('value', Number(event.target.value))}
                  />
                </Field>
                <Field
                  label={t('promoCodes.minPurchase')}
                  htmlFor="minimumAmount"
                  hint={t('promoCodes.minPurchaseHint')}
                >
                  <Input
                    id="minimumAmount"
                    type="number"
                    min={0}
                    step="0.01"
                    value={draft.minimumAmount}
                    disabled={busy}
                    onChange={(event) =>
                      set('minimumAmount', Number(event.target.value))
                    }
                  />
                </Field>
              </div>

              <Field
                label={t('promoCodes.expires')}
                htmlFor="expiry"
                hint={t('promoCodes.expiryHint')}
              >
                <Input
                  id="expiry"
                  type="date"
                  value={expiry}
                  disabled={busy}
                  onChange={(event) => setExpiry(event.target.value)}
                />
              </Field>
            </Section>

            <Section title={t('promoCodes.scope')}>
              <Field
                label={t('promoCodes.store')}
                hint={t('promoCodes.storeHint')}
              >
                <Select
                  value={draft.businessId ?? GLOBAL}
                  disabled={busy}
                  onValueChange={(value) =>
                    set('businessId', value === GLOBAL ? null : value)
                  }
                >
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={GLOBAL}>
                      {t('promoCodes.global')}
                    </SelectItem>
                    {(businesses ?? []).map((business) => (
                      <SelectItem key={business.id} value={business.id}>
                        {business.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>

              <div className="grid gap-3 sm:grid-cols-2">
                <Field label={t('promoCodes.appliesTo')}>
                  <Select
                    value={String(draft.appliesTo)}
                    disabled={busy}
                    onValueChange={(value) => set('appliesTo', Number(value))}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.values(APPLIES_TO).map((value) => (
                        <SelectItem key={value} value={String(value)}>
                          {t(`promoCodes.appliesTo${value}`)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
                {/* Labelled for what it decides. The original called this
                    "Type", which is a different column entirely. */}
                <Field
                  label={t('promoCodes.usageLimit')}
                  hint={t('promoCodes.usageLimitHint')}
                >
                  <Select
                    value={String(draft.applicationType)}
                    disabled={busy}
                    onValueChange={(value) =>
                      set('applicationType', Number(value))
                    }
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.values(APPLICATION_TYPE).map((value) => (
                        <SelectItem key={value} value={String(value)}>
                          {t(`promoCodes.usage${value}`)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
              </div>
            </Section>

            {kind === 'customer' && (
              <Section title={t('promoCodes.notification')}>
                <Toggle
                  label={t('promoCodes.notifyCustomer')}
                  hint={t('promoCodes.notifyHint')}
                  checked={notify}
                  disabled={busy}
                  onChange={setNotify}
                />
                {notify && (
                  <Field label={t('promoCodes.message')} htmlFor="message">
                    <Textarea
                      id="message"
                      rows={3}
                      value={draft.message ?? ''}
                      disabled={busy}
                      onChange={(event) => set('message', event.target.value)}
                    />
                  </Field>
                )}
              </Section>
            )}
          </div>
        )}

        <div className="flex justify-end gap-2 border-t p-4">
          <Button
            variant="outline"
            disabled={busy}
            onClick={() => onOpenChange(false)}
          >
            {t('common.cancel')}
          </Button>
          <Button disabled={!canSave || busy} onClick={submit}>
            {busy && <Loader2 className="animate-spin" />}
            {isEdit ? t('common.save') : t('promoCodes.create')}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
